if (!customElements.get('bark-belt-product')) {
  class BarkBeltProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.slides = Array.from(this.querySelectorAll('[data-bb-slide]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-bb-gallery-thumb]'));
      this.counter = this.querySelector('[data-bb-gallery-counter]');
      this.mainQuantity = this.querySelector('[data-bb-main-quantity]');
      this.stickyQuantity = this.querySelector('[data-bb-sticky-quantity]');
      this.sizeSelect = this.querySelector('[data-bb-size-select]');
      this.activeSlide = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.touchStartX = null;

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
      this.bindSwipe();

      const initialInput = this.querySelector('[data-bb-variant-input]');
      const initialSource = initialInput
        ? this.getVariantSource(initialInput.value)
        : this.querySelector('[data-bb-size-trigger][data-price-cents]');

      this.variantOptionValues = this.getSourceOptions(initialSource);
      if (initialSource) this.selectVariant(initialSource.dataset.variantId, false);
      this.showSlide(this.activeSlide, false);
      this.syncQuantity();
    }

    handleClick(event) {
      const previous = event.target.closest('[data-bb-gallery-prev]');
      const next = event.target.closest('[data-bb-gallery-next]');
      const thumbnail = event.target.closest('[data-bb-gallery-thumb]');
      const optionTrigger = event.target.closest('[data-bb-option-trigger]');
      const sizeTrigger = event.target.closest('[data-bb-size-trigger]');
      const sizeGuideTrigger = event.target.closest('[data-bb-size-guide-trigger]');

      if (previous) this.showSlide(this.activeSlide - 1);
      if (next) this.showSlide(this.activeSlide + 1);
      if (thumbnail) this.showSlide(Number(thumbnail.dataset.index));
      if (optionTrigger) this.selectProductOption(optionTrigger.dataset.optionPosition, optionTrigger.dataset.optionValue);
      if (sizeTrigger) this.selectVariant(sizeTrigger.dataset.variantId);
      if (sizeGuideTrigger) this.openSizeGuide();
    }

    handleChange(event) {
      if (event.target.matches('[data-bb-size-select]')) {
        if (event.target.dataset.bbOptionPosition) {
          this.selectProductOption(event.target.dataset.bbOptionPosition, event.target.value);
        } else {
          this.selectVariant(event.target.value);
        }
      }

      if (event.target.matches('[data-bb-main-quantity]')) {
        this.syncQuantity();
      }
    }

    bindSwipe() {
      const stage = this.querySelector('.bb-hero__stage');
      if (!stage || this.slides.length < 2) return;

      stage.addEventListener('touchstart', (event) => {
        this.touchStartX = event.changedTouches[0]?.clientX ?? null;
      }, { passive: true });

      stage.addEventListener('touchend', (event) => {
        if (this.touchStartX === null) return;
        const touchEndX = event.changedTouches[0]?.clientX ?? this.touchStartX;
        const distance = touchEndX - this.touchStartX;
        this.touchStartX = null;
        if (Math.abs(distance) < 45) return;
        this.showSlide(this.activeSlide + (distance < 0 ? 1 : -1));
      }, { passive: true });
    }

    showSlide(index, focusThumbnail = true) {
      if (!this.slides.length) return;

      const total = this.slides.length;
      this.activeSlide = ((Number(index) % total) + total) % total;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.activeSlide;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        if (!active) this.pauseMedia(slide);
      });

      this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const active = thumbnailIndex === this.activeSlide;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (this.counter) this.counter.textContent = `${this.activeSlide + 1} / ${total}`;
      if (focusThumbnail && this.thumbnails[this.activeSlide]) {
        const activeThumbnail = this.thumbnails[this.activeSlide];
        const thumbnailRail = activeThumbnail.closest('.bb-hero__thumbs');

        if (thumbnailRail) {
          const railRect = thumbnailRail.getBoundingClientRect();
          const thumbnailRect = activeThumbnail.getBoundingClientRect();
          const targetLeft = thumbnailRail.scrollLeft
            + thumbnailRect.left
            - railRect.left
            - ((railRect.width - thumbnailRect.width) / 2);

          thumbnailRail.scrollTo({
            left: Math.max(0, targetLeft),
            behavior: 'smooth'
          });
        }
      }
    }

    openSizeGuide() {
      const sizeChartApp = this.querySelector('[data-bb-size-chart-app]');
      const sizeChartControl = sizeChartApp?.querySelector('button, a, [role="button"]');
      if (sizeChartControl) {
        sizeChartControl.click();
        return;
      }

      const sizeGuideSlide = this.querySelector('[data-bb-size-guide-slide]');
      if (sizeGuideSlide) {
        this.showSlide(Number(sizeGuideSlide.dataset.index));
        const stage = this.querySelector('.bb-hero__stage');
        if (window.theme?.scrollToRevealElement) {
          window.theme.scrollToRevealElement(stage);
        } else {
          stage?.scrollIntoView({ behavior: 'smooth', block: 'center'});
        }
        return;
      }

      document.getElementById(`size-chart-${this.querySelector('[data-bb-variant-input]')?.form?.dataset.productId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    pauseMedia(container) {
      container.querySelectorAll('video').forEach((video) => video.pause());
      container.querySelectorAll('iframe').forEach((iframe) => {
        if (iframe.src.includes('youtube')) {
          iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        }
      });
    }

    getVariantSource(variantId) {
      return Array.from(this.querySelectorAll('[data-bb-variant-source], [data-bb-size-trigger][data-price-cents]'))
        .find((trigger) => trigger.dataset.variantId === String(variantId));
    }

    getSourceOptions(source) {
      if (!source?.dataset.options) return [];
      try {
        const options = JSON.parse(source.dataset.options);
        return Array.isArray(options) ? options : [];
      } catch (error) {
        return [];
      }
    }

    selectProductOption(position, value) {
      const optionIndex = Math.max(0, Number(position) - 1);
      const nextOptions = [...(this.variantOptionValues || [])];
      nextOptions[optionIndex] = value;

      const source = Array.from(this.querySelectorAll('[data-bb-variant-source]')).find((candidate) => {
        const candidateOptions = this.getSourceOptions(candidate);
        return candidateOptions.length === nextOptions.length
          && candidateOptions.every((option, index) => option === nextOptions[index]);
      });

      if (!source) return false;
      this.selectVariant(source.dataset.variantId);
      return true;
    }

    selectVariant(variantId, updateUrl = true) {
      const source = this.getVariantSource(variantId);
      if (!source) return;

      const available = source.dataset.available === 'true';
      const title = source.dataset.variantTitle || '';
      const price = source.dataset.price || '';
      const priceCents = Number(source.dataset.priceCents || 0);
      const comparePriceCents = Number(source.dataset.comparePriceCents || 0);
      const sourceOptions = this.getSourceOptions(source);
      if (sourceOptions.length) this.variantOptionValues = sourceOptions;

      this.querySelectorAll('[data-bb-variant-input]').forEach((input) => {
        input.value = variantId;
      });

      this.querySelectorAll('[data-bb-size-trigger]').forEach((trigger) => {
        const selected = trigger.dataset.variantId === String(variantId);
        trigger.classList.toggle('is-selected', selected);
        if (trigger.getAttribute('role') === 'radio') {
          trigger.setAttribute('aria-checked', selected ? 'true' : 'false');
        } else {
          trigger.setAttribute('aria-pressed', selected ? 'true' : 'false');
        }
      });

      this.querySelectorAll('[data-bb-option-trigger]').forEach((trigger) => {
        const optionIndex = Math.max(0, Number(trigger.dataset.optionPosition) - 1);
        const selected = sourceOptions[optionIndex] === trigger.dataset.optionValue;
        trigger.classList.toggle('is-selected', selected);
        if (trigger.getAttribute('role') === 'radio') {
          trigger.setAttribute('aria-checked', selected ? 'true' : 'false');
        } else {
          trigger.setAttribute('aria-pressed', selected ? 'true' : 'false');
        }

        if (selected && !trigger.hasAttribute('data-bb-size-choice-trigger')) {
          const selectedColor = this.querySelector('[data-bb-selected-color]');
          if (selectedColor) selectedColor.textContent = trigger.dataset.optionValue || '';
        }
      });

      if (this.sizeSelect) {
        if (this.sizeSelect.dataset.bbOptionPosition) {
          const sizeIndex = Math.max(0, Number(this.sizeSelect.dataset.bbOptionPosition) - 1);
          this.sizeSelect.value = sourceOptions[sizeIndex] || '';
        } else {
          this.sizeSelect.value = variantId;
        }
      }

      const priceElement = this.querySelector('[data-bb-price]');
      if (priceElement) priceElement.textContent = price;

      const comparePrice = this.querySelector('[data-bb-compare-price]');
      if (comparePrice) {
        comparePrice.hidden = comparePriceCents <= priceCents;
        comparePrice.textContent = source.dataset.comparePrice || '';
      }

      this.querySelectorAll('[data-bb-atc]').forEach((button) => {
        button.disabled = !available;
        button.setAttribute('aria-disabled', available ? 'false' : 'true');
        const label = button.querySelector('[data-bb-atc-label]');
        if (label) label.textContent = available ? this.dataset.addLabel : this.dataset.soldOutLabel;
      });

      const stickyDescription = this.querySelector('[data-bb-sticky-description]');
      if (stickyDescription) stickyDescription.textContent = `${title} · ${price}`;

      this.selectedPriceCents = priceCents;
      this.syncQuantity();
      this.showVariantMedia(source.dataset.featuredMediaId);

      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url);
      }

      this.dispatchEvent(new CustomEvent('bb:variant:change', {
        bubbles: true,
        detail: { variantId, available, options: sourceOptions }
      }));
    }

    showVariantMedia(mediaId) {
      if (!mediaId) return;
      const slideIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (slideIndex >= 0) this.showSlide(slideIndex, false);
    }

    syncQuantity() {
      const quantity = Math.max(1, Number.parseInt(this.mainQuantity?.value || '1', 10) || 1);
      if (this.mainQuantity) this.mainQuantity.value = quantity;
      if (this.stickyQuantity) this.stickyQuantity.value = quantity;

      const stickyTotal = this.querySelector('[data-bb-sticky-total]');
      if (stickyTotal && Number.isFinite(this.selectedPriceCents)) {
        stickyTotal.textContent = `· ${this.formatMoney(this.selectedPriceCents * quantity)}`;
      }
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: this.dataset.currency || 'USD'
        }).format(cents / 100);
      } catch (error) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }
  }

  customElements.define('bark-belt-product', BarkBeltProduct);
}
